/**
 * ustvaExport — reine Export-Gate-Matrix + Payload-Mapper für den UStVA-XML-Export
 * (M-U8, `architecture-ustva-xml.md` §5.6/§6.4/§8.1).
 *
 * KEINE Firebase-/React-Imports — nur Typen (compile-time erased) + reine Helfer.
 * Trivial unit-testbar.
 *
 * Zwei Kern-Verträge:
 *   1. `evaluateUstvaExportGate` — die fail-closed Gate-Matrix (B2–B11, W1–W3, Info-
 *      Nullmeldung). Der Anspruch ist Steuerberater-Niveau: lieber blockieren als still
 *      eine sachlich falsche Anmeldung liefern.
 *   2. `toUstvaXmlRequest` — der Payload-Mapper. Er erzeugt EXAKT den eingefrorenen
 *      Vertrag (§5.1, gespiegelt in `__fixtures__/ustva/*.json`). Feld-Reihenfolge ist
 *      bewusst identisch zu den Fixtures, damit `JSON.stringify` byte-genau trifft.
 *
 * @module ustvaExport
 */

import type { VatReturnFigures } from './vatReturn';
import type { SummaryPeriod } from './expenseSummary';

// ─── Bundesland (§5.3) ────────────────────────────────────────────────────────

/** 16-Länder-Code (identisch zur PHP-Tabelle in M-U1). */
export type BundeslandCode =
  | 'BW' | 'BY' | 'BE' | 'BB' | 'HB' | 'HH' | 'HE' | 'MV'
  | 'NI' | 'NW' | 'RP' | 'SL' | 'SN' | 'ST' | 'SH' | 'TH';

/** Alle 16 Codes, alphabetisch nach Landesname (Select-Reihenfolge, §5.3). */
export const BUNDESLAND_CODES: readonly BundeslandCode[] = [
  'BW', 'BY', 'BE', 'BB', 'HB', 'HH', 'HE', 'MV',
  'NI', 'NW', 'RP', 'SL', 'SN', 'ST', 'SH', 'TH',
];

// ─── Fehler-Codes (§5.4) — geschlossene, i18n-mapbare Liste ──────────────────────

/** Stabile Server-Fehlercodes (422). FE mappt Code → i18n-Key; unbekannt ⇒ generisch. */
export type UstvaErrorCode =
  | 'unsupported_schema_year'
  | 'invalid_period_code'
  | 'period_year_mismatch'
  | 'steuernummer_invalid'
  | 'steuernummer_missing'
  | 'negative_kz'
  | 'reverse_charge_split_unsupported'
  | 'acquisition_rate_mismatch'
  | 'kz83_drift'
  | 'kz_unproven_without_xsd'
  | 'gate_violation'
  | 'missing_unternehmer_field'
  | 'structural_invalid'
  | 'xsd_invalid';

// ─── FE-Gate-Typen (§5.6) ────────────────────────────────────────────────────────

/** Ein harter Blocker — Export unmöglich, Button disabled + Begründung. */
export interface UstvaGateBlocker {
  code:
    | 'ist_mode'
    | 'small_business'
    | 'year_period'
    | 'unclassified'
    | 'unclassified_expense'
    | 'reverse_charge_split'
    | 'acquisition_rate_mismatch'
    | 'negative_kz'
    | 'down_payments'
    | 'missing_master_data';
  // Rev. 3 (§7.5): 'down_payment_probe_failed' ENTFÄLLT — der Detektor ist pure/synchron,
  // ein Probe-Fehlpfad existiert nicht mehr.
  /** Optionaler Anzeige-Kontext (z. B. Betrag/Anzahl für den Begründungstext). */
  count?: number;
  amountCents?: number;
}

/** Eine Warnung — Export erst nach expliziter Bestätigungs-Checkbox. */
export interface UstvaGateWarning {
  code: 'excluded_drafts' | 'fallback_dates' | 'null_report' | 'kz22_december';
  count?: number;
}

/** Ergebnis der Gate-Auswertung. */
export type UstvaGateResult =
  | { status: 'blocked'; blockers: UstvaGateBlocker[] }
  | { status: 'warn'; warnings: UstvaGateWarning[] }
  | { status: 'ok'; warnings: [] };

// ─── Anzahlungs-Detektor (§7.5, Rev. 3 — ersetzt die Firestore-Probe) ─────────────

/**
 * Subset-Sicht eines bereits geladenen `invoices`-Docs, soweit der Anzahlungs-Detektor
 * sie liest (§7.5, K1–K6). ALLE Felder optional — der Altbestand variiert, jedes Doc
 * kann die Marker top-level ODER in `rawData` tragen. Strukturelle Teilmenge des
 * Page-`InvoiceDoc` (dessen `rawData` ist `Partial<InvoiceData>`), damit die Page ihren
 * geladenen Datensatz 1:1 durchreichen kann.
 */
export interface DownPaymentProbeDoc {
  invoiceNumber?: string;
  isDownPayment?: boolean;
  type?: string;
  notes?: string | null;
  subject?: string | null;
  rawData?: {
    isDownPayment?: boolean;
    type?: string;
    downPaymentType?: string;
    isFinalInvoice?: boolean;
    relatedDownPayments?: unknown[];
    notes?: string | null;
    subject?: string | null;
    items?: ReadonlyArray<{ name?: string | null; description?: string | null } | null | undefined>;
  } | null;
}

/** Ergebnis der In-Memory-Anzahlungs-Detektion (§5.6). */
export interface DownPaymentDetection {
  present: boolean;
  matchCount: number;
  firstMatchInvoiceNumber: string | null;
}

/** Marker-Regex für notes/subject/items (case-insensitiv, §7.5 K4/K5). */
const DOWN_PAYMENT_MARKER = /abschlag|anzahlung/i;

/** true, wenn `s` einen Anzahlungs-/Abschlags-Marker enthält (case-insensitiv). */
function hasMarker(s: string | null | undefined): boolean {
  return typeof s === 'string' && DOWN_PAYMENT_MARKER.test(s);
}

/**
 * Prüft ein einzelnes Doc gegen die ODER-Kette K1–K6 (§7.5). Jede Prüfung defensiv
 * auf top-level UND `rawData`. Superset-Regel: der Detektor DARF strenger sein als die
 * PHP-Heuristik (Fehltreffer blockt nur den Export = fail-closed-konform), NIE milder.
 */
function docIsDownPayment(doc: DownPaymentProbeDoc): boolean {
  const raw = doc.rawData ?? undefined;

  // K1 — Struktur-Flag `isDownPayment`.
  if (doc.isDownPayment === true || raw?.isDownPayment === true) return true;

  // K2 — `type === 'downPayment'`.
  if (doc.type === 'downPayment' || raw?.type === 'downPayment') return true;

  // K3 — `rawData.downPaymentType` nicht-leer.
  if (typeof raw?.downPaymentType === 'string' && raw.downPaymentType.trim().length > 0) {
    return true;
  }

  // K4 — notes/subject enthält Marker (top-level ∨ rawData).
  if (
    hasMarker(doc.notes) ||
    hasMarker(doc.subject) ||
    hasMarker(raw?.notes) ||
    hasMarker(raw?.subject)
  ) {
    return true;
  }

  // K5 — irgendein Item-Name/-Beschreibung enthält Marker.
  const items = raw?.items;
  if (
    Array.isArray(items) &&
    items.some((it) => hasMarker(it?.name) || hasMarker(it?.description))
  ) {
    return true;
  }

  // K6 — Schlussrechnungs-Marker (isFinalInvoice ∨ nicht-leeres relatedDownPayments).
  if (raw?.isFinalInvoice === true) return true;
  const related = raw?.relatedDownPayments;
  if (Array.isArray(related) && related.length > 0) return true;

  return false;
}

/**
 * Purer In-Memory-Detektor für Anzahlungs-/Abschlags- und Schlussrechnungen (§7.5,
 * Rev. 3). TS-Port der PHP-Heuristik `DownPaymentRepository::isDownPaymentInvoice`
 * (+ Schlussrechnungs-Marker), läuft über die BEREITS geladenen `invoices`-Docs der
 * Page — kein IO, kein Fehlpfad (Gate B9).
 *
 * Scope: user-level über den GESAMTEN Datensatz, bewusst NICHT perioden-gefiltert —
 * der Steuerentstehungszeitpunkt einer Anzahlung ist die Vereinnahmung, die hier
 * unsichtbar ist (§7.5). Storno-Docs von Abschlagsrechnungen zählen mit (Existenz).
 *
 * @param docs Bereits geladene `invoices`-Docs (Subset-Sicht).
 * @returns `{ present, matchCount, firstMatchInvoiceNumber }` — `present` steuert B9,
 *          `matchCount`/`firstMatchInvoiceNumber` speisen den Panel-Detailtext.
 */
export function detectDownPaymentPresence(
  docs: readonly DownPaymentProbeDoc[],
): DownPaymentDetection {
  let matchCount = 0;
  let firstMatchInvoiceNumber: string | null = null;

  for (const doc of docs) {
    if (!docIsDownPayment(doc)) continue;
    matchCount += 1;
    if (
      firstMatchInvoiceNumber === null &&
      typeof doc.invoiceNumber === 'string' &&
      doc.invoiceNumber.trim().length > 0
    ) {
      firstMatchInvoiceNumber = doc.invoiceNumber;
    }
  }

  return { present: matchCount > 0, matchCount, firstMatchInvoiceNumber };
}

// ─── Seller-Input (strukturelle Teilmenge von `Seller`) ──────────────────────────

/**
 * Nur die für Gate + Mapper benötigten Stammdaten. `Seller` (invoiceTypes) ist
 * strukturell zuweisbar — die Page reicht `sellerData` direkt durch.
 */
export interface UstvaSellerInput {
  name: string | null;
  street: string | null;
  zip: string | null;
  city: string | null;
  taxNumber: string | null;
  bundesland?: BundeslandCode;
  phone?: string | null;
  email?: string | null;
  taxationMode?: 'soll' | 'ist';
  isSmallBusiness?: boolean;
}

// ─── Request-Vertrag (§5.1 — EINGEFROREN, gespiegelt in __fixtures__) ─────────────

/**
 * Der eingefrorene Payload-Vertrag von `POST /ustva/generate-xml`. Feld-Reihenfolge
 * bewusst identisch zu `__fixtures__/ustva/*.json` — der Mapper erzeugt sie in genau
 * dieser Reihenfolge, damit `JSON.stringify(req, null, 4)` byte-genau trifft.
 */
export interface UstvaGenerateXmlRequest {
  schemaYear: number;
  period: { jahr: number; zeitraumCode: string };
  kz: {
    kz81BaseCents: number;
    kz86BaseCents: number;
    kz35BaseCents: number;
    kz36VatCents: number;
    kz41BaseCents: number;
    kz43BaseCents: number;
    kz48BaseCents: number;
    kz60BaseCents: number;
    kz84BaseCents: number;
    kz85VatCents: number;
    kz89BaseCents: number;
    kz89VatCents: number;
    kz66VatCents: number;
    kz61VatCents: number;
    kz67VatCents: number;
    kz83PayableCents: number;
  };
  unternehmer: {
    name: string;
    strasse: string;
    hausnummer: string | null;
    plz: string;
    ort: string;
    telefon: string | null;
    email: string | null;
  };
  steuernummer: { landesformat: string; bundesland: BundeslandCode };
  gates: {
    taxationMode: 'soll';
    smallBusiness: false;
    unclassifiedCount: number;
    unclassifiedExpenseCount: number;
    downPaymentsPresent: boolean;
    excludedDraftVatOwedCount: number;
    fallbackDateCount: number;
    warnAcknowledged: boolean;
  };
}

// ─── Kleine reine Helfer ─────────────────────────────────────────────────────────

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Perioden-Code für den ELSTER-Vertrag: Monat `"01".."12"`, Quartal `"41".."44"`.
 * Jahres-Periode ⇒ `null` (Export dafür über B4 geblockt).
 *
 * @param period Aufgelöster Zeitraum.
 * @returns Zwei-/zweistelliger Code oder `null` bei `kind === 'year'`.
 */
export function toZeitraumCode(period: SummaryPeriod): string | null {
  if (period.kind === 'month') {
    return pad2(period.month ?? 0);
  }
  if (period.kind === 'quarter') {
    return String(40 + (period.quarter ?? 0));
  }
  return null; // year
}

/**
 * Trennt eine einzeilige Straße fail-soft in Straßenname + Hausnummer.
 * Regel (§5.6/§6.4): das LETZTE mit einer Ziffer beginnende Token ist die Hausnummer;
 * kein solches Token (oder keine verbleibende Straße) ⇒ alles bleibt Straße, `hausnummer`
 * = null (nicht blockierend — der Nutzer kann im Portal nachtragen).
 *
 * @param street Einzeiliger Straßen-String (z. B. `"Flurgasse 76"`).
 * @returns `{ strasse, hausnummer }` — `hausnummer` ist `null`, wenn kein Split möglich.
 */
export function splitStreet(street: string): { strasse: string; hausnummer: string | null } {
  const trimmed = (street ?? '').trim();
  if (trimmed.length === 0) {
    return { strasse: '', hausnummer: null };
  }
  const tokens = trimmed.split(/\s+/);
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    if (/^\d/.test(tokens[i])) {
      const strasse = tokens.slice(0, i).join(' ');
      if (strasse.length === 0) {
        // Straße besteht nur aus Ziffern-Token ⇒ nicht sinnvoll trennbar.
        return { strasse: trimmed, hausnummer: null };
      }
      return { strasse, hausnummer: tokens.slice(i).join(' ') };
    }
  }
  return { strasse: trimmed, hausnummer: null };
}

function isBlank(v: string | null | undefined): boolean {
  return v == null || v.trim().length === 0;
}

/** Die Vertrags-KZ, deren Negativität B8/I-4 verletzt (kz83 ausgenommen). */
function anyContractKzNegative(f: VatReturnFigures): boolean {
  return (
    f.kz81BaseCents < 0 ||
    f.kz86BaseCents < 0 ||
    f.kz35BaseCents < 0 ||
    f.kz36VatCents < 0 ||
    f.kz41BaseCents < 0 ||
    f.kz43BaseCents < 0 ||
    f.kz48BaseCents < 0 ||
    f.kz60BaseCents < 0 ||
    f.kz84BaseCents < 0 ||
    f.kz85VatCents < 0 ||
    f.kz89BaseCents < 0 ||
    f.kz89VatCents < 0 ||
    f.kz66VatCents < 0 ||
    f.kz61VatCents < 0 ||
    f.kz67VatCents < 0
  );
}

// ─── Gate-Matrix (§8.1) ──────────────────────────────────────────────────────────

/**
 * Wertet die fail-closed Export-Gate-Matrix aus (B2–B11, W1–W3).
 *
 * Reine Funktion, wirft nie. Reihenfolge des Ergebnisses: bei ≥1 Blocker `blocked`
 * (keine Checkbox), sonst bei ≥1 Warnung `warn` (Export erst nach Checkbox), sonst `ok`.
 * Die Nullmeldungs-/„0 Belege"-Info ist KEIN Gate-Ergebnis — sie wird im Panel aus
 * `figures` abgeleitet (§8.1 INFO).
 *
 * @param figures            Aggregierte UStVA-Kennzahlen (`buildVatReturn`).
 * @param configuredMode     `sellerData.taxationMode` — NICHT `figures.taxationMode`
 *                           (der ist hart 'soll'). Nur `'ist'` blockt (B2).
 * @param period             Aufgelöster Zeitraum (B4 blockt `kind === 'year'`).
 * @param fallbackDateCount  Belege ohne BT-72-Leistungsdatum in der Periode (W2).
 * @param seller             Stammdaten für B10 (Name/Straße/PLZ/Ort/Steuernummer/Bundesland).
 * @param downPaymentsPresent Ergebnis des reinen In-Memory-Detektors (§7.5, Rev. 3):
 *                           `true` (Anzahlung/Abschlag/Schlussrechnung erkannt) ⇒ B9-Block,
 *                           `false` (keine). Kein `'probe_failed'`-Pfad mehr (pure/synchron).
 * @returns Gate-Ergebnis (`blocked` | `warn` | `ok`).
 */
export function evaluateUstvaExportGate(
  figures: VatReturnFigures,
  configuredMode: 'soll' | 'ist' | undefined,
  period: SummaryPeriod,
  fallbackDateCount: number,
  seller: UstvaSellerInput,
  downPaymentsPresent: boolean,
): UstvaGateResult {
  const blockers: UstvaGateBlocker[] = [];

  // B2 — Ist-Versteuerer: Soll-Zahlen wären materiell falsch (M8c nicht gebaut).
  if (configuredMode === 'ist') {
    blockers.push({ code: 'ist_mode' });
  }

  // B3 — §19-Kleinunternehmer (beide Varianten, auch mit RC-Duty).
  if (figures.smallBusiness === true) {
    blockers.push({ code: 'small_business' });
  }

  // B4 — Jahres-Periode ist ein anderes Formular.
  if (period.kind === 'year') {
    blockers.push({ code: 'year_period' });
  }

  // B5 — wissentlich unvollständige Ausgangs-KZ (unklassifizierte Einnahmen).
  if (figures.unclassifiedIncome.count > 0) {
    blockers.push({
      code: 'unclassified',
      count: figures.unclassifiedIncome.count,
      amountCents: figures.unclassifiedIncome.grossCents,
    });
  }

  // B6 — §13b-Split (Kz84/85) im Datenmodell nicht trennbar (§18-Zuordnungsrisiko).
  if (figures.kz84BaseCents !== 0 || figures.kz85VatCents !== 0) {
    blockers.push({ code: 'reverse_charge_split' });
  }

  // B7 — Kz89-Satz-Proxy: > 1 € Abweichung vom 19 %-Satz ⇒ gemischte Sätze.
  // (Y-2, §8.1: PROXY, kein Beweis — Netto ≤ 8,33 € rutscht durch die 100-¢-Toleranz;
  // zahllast-neutral bei Vollabzug, aber KZ-Zuordnung unpräzise. Vor GA-Flip im Runbook.)
  if (
    Math.abs(figures.kz89VatCents - Math.round(figures.kz89BaseCents * 0.19)) > 100
  ) {
    blockers.push({ code: 'acquisition_rate_mismatch' });
  }

  // B8 — negative Vertrags-KZ (Storno-Überhang) — ELSTER akzeptiert sie nicht.
  if (anyContractKzNegative(figures)) {
    blockers.push({ code: 'negative_kz' });
  }

  // B9 — Anzahlungen/Abschläge/Schlussrechnungen im Datensatz (§7.5): Perioden-Zuordnung
  // + Summen können in beide Richtungen falsch sein (Vereinnahmungs-Zeitpunkt, Verrechnung).
  if (downPaymentsPresent === true) {
    blockers.push({ code: 'down_payments' });
  }

  // B10 — Rahmen-Pflichtstammdaten unvollständig.
  if (
    isBlank(seller.taxNumber) ||
    seller.bundesland == null ||
    isBlank(seller.name) ||
    isBlank(seller.street) ||
    isBlank(seller.zip) ||
    isBlank(seller.city)
  ) {
    blockers.push({ code: 'missing_master_data' });
  }

  // B11 — VETO-1: eingangsseitig unklassifizierte Ausgaben (EUSt/needs_review) ⇒
  // Vorsteuer untererfasst ⇒ Kz83 ZU HOCH. Blocker (NICHT Warnung).
  if (figures.unclassifiedExpense.count > 0) {
    blockers.push({
      code: 'unclassified_expense',
      count: figures.unclassifiedExpense.count,
      amountCents: figures.unclassifiedExpense.grossCents,
    });
  }

  if (blockers.length > 0) {
    return { status: 'blocked', blockers };
  }

  // ── Warnungen (Export erst nach Checkbox) ──────────────────────────────────────
  const warnings: UstvaGateWarning[] = [];

  // W1 — Entwurfs-Belege mit §13b/IGL-Schuld nicht enthalten.
  if (figures.excludedDraftVatOwedCount > 0) {
    warnings.push({ code: 'excluded_drafts', count: figures.excludedDraftVatOwedCount });
  }

  // W2 — Belege ohne BT-72-Leistungsdatum (Periodisierung über Rechnungsdatum).
  if (fallbackDateCount > 0) {
    warnings.push({ code: 'fallback_dates', count: fallbackDateCount });
  }

  // W3 (Y-1) — Dezember-Anmeldung: Sondervorauszahlung/Kz22-Doppelzahlungs-Risiko.
  if (toZeitraumCode(period) === '12') {
    warnings.push({ code: 'kz22_december' });
  }

  if (warnings.length > 0) {
    return { status: 'warn', warnings };
  }

  return { status: 'ok', warnings: [] };
}

// ─── Payload-Mapper (§5.1) ───────────────────────────────────────────────────────

/**
 * Baut den eingefrorenen `POST /ustva/generate-xml`-Payload aus den Kennzahlen +
 * Stammdaten. Reine Funktion, wirft nie.
 *
 * AUFRUFER-VERTRAG: nur aufrufen, wenn `evaluateUstvaExportGate` NICHT `blocked`
 * liefert (dann sind `taxationMode !== 'ist'`, `smallBusiness === false`, Periode ≠ Jahr,
 * Stammdaten vollständig, keine Anzahlungen). Die Literale `taxationMode: 'soll'` und
 * `smallBusiness: false` sind dadurch garantiert korrekt.
 *
 * Feld-Reihenfolge = Fixture-Reihenfolge (§5.1) ⇒ byte-genaues `JSON.stringify`.
 *
 * @param figures            Aggregierte Kennzahlen.
 * @param seller             Stammdaten (Straße bereits einzeilig; wird hier gesplittet).
 * @param period             Aufgelöster Zeitraum (Monat/Quartal — kein Jahr).
 * @param fallbackDateCount  Anzahl BT-72-Fallback-Belege (Gate-Selbstauskunft).
 * @param downPaymentsPresent Anzahlungs-Probe-Ergebnis (per Aufrufer-Vertrag `false`).
 * @param warnAcknowledged   Hat der Nutzer die Warn-Hinweise bestätigt?
 * @returns Der vollständige, vertragskonforme Request.
 */
export function toUstvaXmlRequest(
  figures: VatReturnFigures,
  seller: UstvaSellerInput,
  period: SummaryPeriod,
  fallbackDateCount: number,
  downPaymentsPresent: boolean,
  warnAcknowledged: boolean,
): UstvaGenerateXmlRequest {
  const { strasse, hausnummer } = splitStreet(seller.street ?? '');

  return {
    schemaYear: period.year,
    period: {
      jahr: period.year,
      zeitraumCode: toZeitraumCode(period) ?? '',
    },
    kz: {
      kz81BaseCents: figures.kz81BaseCents,
      kz86BaseCents: figures.kz86BaseCents,
      kz35BaseCents: figures.kz35BaseCents,
      kz36VatCents: figures.kz36VatCents,
      kz41BaseCents: figures.kz41BaseCents,
      kz43BaseCents: figures.kz43BaseCents,
      kz48BaseCents: figures.kz48BaseCents,
      kz60BaseCents: figures.kz60BaseCents,
      kz84BaseCents: figures.kz84BaseCents,
      kz85VatCents: figures.kz85VatCents,
      kz89BaseCents: figures.kz89BaseCents,
      kz89VatCents: figures.kz89VatCents,
      kz66VatCents: figures.kz66VatCents,
      kz61VatCents: figures.kz61VatCents,
      kz67VatCents: figures.kz67VatCents,
      kz83PayableCents: figures.kz83PayableCents,
    },
    unternehmer: {
      name: seller.name ?? '',
      strasse,
      hausnummer,
      plz: seller.zip ?? '',
      ort: seller.city ?? '',
      telefon: seller.phone ?? null,
      email: seller.email ?? null,
    },
    steuernummer: {
      landesformat: seller.taxNumber ?? '',
      // Per Aufrufer-Vertrag (B10) gesetzt; Cast nur zur Typ-Erfüllung.
      bundesland: seller.bundesland as BundeslandCode,
    },
    gates: {
      taxationMode: 'soll',
      smallBusiness: false,
      unclassifiedCount: figures.unclassifiedIncome.count,
      unclassifiedExpenseCount: figures.unclassifiedExpense.count,
      downPaymentsPresent,
      excludedDraftVatOwedCount: figures.excludedDraftVatOwedCount,
      fallbackDateCount,
      warnAcknowledged,
    },
  };
}
